import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Target, User as UserIcon, Users, Mail, Send, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const MAX_GOAL_LEN = 120;
const GOAL_SLOTS = 3;
const MAX_MESSAGE_LEN = 1000;

const LockerProfilePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [handicap, setHandicap] = useState('');
  const [goals, setGoals] = useState(['', '', '']);

  // Coach link request state
  const [pendingRequest, setPendingRequest] = useState(null);
  const [coachEmail, setCoachEmail] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestingLink, setRequestingLink] = useState(false);

  const loadAll = async () => {
    try {
      const [{ data: prof }, { data: reqData }] = await Promise.all([
        API.get('/locker/profile'),
        API.get('/locker/coach-request'),
      ]);
      setProfile(prof);
      setHandicap(prof.handicap != null ? String(prof.handicap) : '');
      const g = prof.current_focus_goals || [];
      setGoals([0, 1, 2].map((i) => g[i] || ''));
      setPendingRequest(reqData.request);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        current_focus_goals: goals.map((g) => g.trim()).filter(Boolean),
      };
      if (handicap !== '') {
        const h = parseFloat(handicap);
        if (Number.isNaN(h)) {
          toast.error('Handicap must be a number.');
          setSaving(false);
          return;
        }
        payload.handicap = h;
      } else {
        payload.handicap = null;
      }
      const { data } = await API.patch('/locker/profile', payload);
      setProfile(data);
      toast.success('Profile saved.');
      navigate('/locker');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestCoach = async (e) => {
    e.preventDefault();
    if (!coachEmail.trim()) return;
    setRequestingLink(true);
    try {
      await API.post('/locker/coach-request', {
        coach_email: coachEmail.trim().toLowerCase(),
        message: requestMessage.trim() || null,
      });
      toast.success('Request sent. Your coach has been emailed.');
      setCoachEmail('');
      setRequestMessage('');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send request.');
    } finally {
      setRequestingLink(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!pendingRequest || !window.confirm('Cancel this coach request?')) return;
    try {
      await API.delete(`/locker/coach-request/${pendingRequest.id}`);
      toast.success('Request cancelled.');
      setPendingRequest(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]" data-testid="profile-loading">
        <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="locker-profile-page">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-3xl flex items-center gap-3">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-3xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            <UserIcon className="w-4 h-4" /> Profile · settings
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">
            {profile?.full_name || 'Your Locker'}
          </h1>
          <p className="text-[#57534E] text-sm">{profile?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Handicap */}
          <div className="bg-white border border-[#E7E5E4] p-6">
            <label htmlFor="handicap" className="block text-sm font-semibold text-[#1A1A1A] mb-2">Handicap</label>
            <input
              id="handicap"
              type="number"
              step="0.1"
              min="-10"
              max="54"
              value={handicap}
              onChange={(e) => setHandicap(e.target.value)}
              placeholder="e.g. 8.4"
              className="w-full max-w-[240px]"
              data-testid="handicap-input"
            />
            <p className="text-xs text-[#737373] mt-2">A rough number is fine — used only to give your data context.</p>
          </div>

          {/* Current focus goals */}
          <div className="bg-white border border-[#E7E5E4] p-6">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-[#BC562B]" />
              <h2 className="text-sm font-semibold text-[#1A1A1A]">Current focus goals</h2>
            </div>
            <p className="text-xs text-[#737373] mb-4">Up to {GOAL_SLOTS} short, behavioural goals — what you're working on right now.</p>
            <div className="space-y-3">
              {goals.map((g, i) => (
                <div key={i}>
                  <input
                    type="text"
                    value={g}
                    maxLength={MAX_GOAL_LEN}
                    onChange={(e) => setGoals(goals.map((x, idx) => (idx === i ? e.target.value : x)))}
                    placeholder={i === 0 ? 'e.g. Commit fully to my pre-shot routine' : i === 1 ? 'e.g. Reset within 10 seconds after a bogey' : 'e.g. Trust the target, not the swing'}
                    className="w-full"
                    data-testid={`goal-input-${i}`}
                  />
                  <p className="text-[10px] text-[#A8A29E] mt-1 text-right">{g.length}/{MAX_GOAL_LEN}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Coach link */}
          <div className="bg-white border border-[#E7E5E4] p-6" data-testid="coach-link-card">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#1A2E1D]" />
              <h2 className="text-sm font-semibold text-[#1A1A1A]">Your coach</h2>
            </div>

            {profile?.coach ? (
              <div className="bg-[#FAFAF9] border border-[#E7E5E4] p-4" data-testid="coach-link-active">
                <p className="font-semibold text-[#1A1A1A]">{profile.coach.full_name || 'Coach'}</p>
                <p className="text-sm text-[#57534E] inline-flex items-center gap-1 mt-1">
                  <Mail className="w-3.5 h-3.5" /> {profile.coach.email}
                </p>
                <p className="text-xs text-[#737373] mt-3">Your coach has read-only access to your Locker and can leave notes on rounds.</p>
              </div>
            ) : pendingRequest ? (
              <div className="bg-[#FFF4ED] border border-[#BC562B]/30 p-4" data-testid="coach-link-pending">
                <div className="flex items-start gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#BC562B] mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A] text-sm">Pending coach approval</p>
                    <p className="text-sm text-[#57534E] mt-0.5">
                      You've requested <strong>{pendingRequest.coach?.full_name || pendingRequest.coach?.email}</strong>. They've been emailed and will respond shortly.
                    </p>
                  </div>
                </div>
                {pendingRequest.message && (
                  <p className="text-xs text-[#57534E] mt-2 pl-6 italic border-l-2 border-[#BC562B]/30 ml-0 pl-3">"{pendingRequest.message}"</p>
                )}
                <button
                  type="button"
                  onClick={handleCancelRequest}
                  className="mt-3 inline-flex items-center gap-1 text-xs text-[#BC562B] hover:text-[#9A4520] font-semibold"
                  data-testid="cancel-coach-request-btn"
                >
                  <X className="w-3.5 h-3.5" /> Cancel request
                </button>
              </div>
            ) : (
              <div data-testid="coach-link-empty">
                <p className="text-sm text-[#57534E] mb-4">
                  Already know your coach's email? Send them a link request — they'll be able to see your Locker and leave round notes.
                </p>
                <div className="space-y-3 bg-[#FAFAF9] border border-[#E7E5E4] p-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-1.5" htmlFor="coach-email-input">
                      Coach email
                    </label>
                    <input
                      id="coach-email-input"
                      type="email"
                      value={coachEmail}
                      onChange={(e) => setCoachEmail(e.target.value)}
                      placeholder="coach@example.com"
                      className="w-full"
                      data-testid="coach-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-1.5" htmlFor="coach-msg-input">
                      Optional message
                    </label>
                    <textarea
                      id="coach-msg-input"
                      value={requestMessage}
                      onChange={(e) => setRequestMessage(e.target.value)}
                      placeholder="Anything you'd like them to know — e.g. what you're working on."
                      rows={3}
                      maxLength={MAX_MESSAGE_LEN}
                      className="w-full"
                      data-testid="coach-request-message"
                    />
                    <p className="text-[10px] text-[#A8A29E] mt-1 text-right">{requestMessage.length}/{MAX_MESSAGE_LEN}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRequestCoach}
                    disabled={requestingLink || !coachEmail.trim()}
                    className="inline-flex items-center gap-2 bg-[#1A2E1D] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0F1F12] transition-colors disabled:opacity-50"
                    data-testid="send-coach-request-btn"
                  >
                    <Send className="w-3.5 h-3.5" /> {requestingLink ? 'Sending…' : 'Send link request'}
                  </button>
                </div>
                <p className="text-xs text-[#737373] mt-3">
                  Prefer help finding a coach? Email <a className="underline" href="mailto:scott@ritchiegolf.co.nz">scott@ritchiegolf.co.nz</a>.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Link to="/locker" className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9] transition-colors">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] disabled:opacity-50"
              data-testid="profile-save-btn"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default LockerProfilePage;
